import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, MapPin, Clock, CalendarCheck, Zap } from 'lucide-react';

const TIME_SLOTS_1H = ['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00'];
const TIME_SLOTS_2H = ['08:00','10:00','12:00','14:00','16:00'];

export function TimeSlotPicker({ value, onChange, label = "Start Time", conflictTimes = [] }) {
  const [mode, setMode] = useState('slots');

  return (
    <div data-testid="time-slot-picker">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium text-zinc-700">{label}</span>
        <div className="flex gap-1">
          <button type="button" onClick={() => setMode('slots')}
            className={`text-[10px] px-2 py-0.5 rounded ${mode === 'slots' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600'}`}
            data-testid="time-mode-slots">Slots</button>
          <button type="button" onClick={() => setMode('custom')}
            className={`text-[10px] px-2 py-0.5 rounded ${mode === 'custom' ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600'}`}
            data-testid="time-mode-custom">Custom</button>
        </div>
      </div>
      {mode === 'slots' ? (
        <div className="grid grid-cols-5 gap-1">
          {TIME_SLOTS_1H.map(slot => {
            const isConflict = conflictTimes.includes(slot);
            const isSelected = value === slot;
            return (
              <button key={slot} type="button"
                onClick={() => !isConflict && onChange(slot)}
                className={`py-1.5 rounded text-xs font-medium transition-all border ${
                  isSelected ? 'bg-zinc-950 text-white border-zinc-950' :
                  isConflict ? 'bg-red-50 text-red-400 border-red-200 cursor-not-allowed line-through' :
                  'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400'
                }`}
                data-testid={`time-slot-${slot}`}
                disabled={isConflict}
              >
                {slot}
              </button>
            );
          })}
        </div>
      ) : (
        <input type="time" value={value || ''} onChange={(e) => onChange(e.target.value)}
          className="w-full h-9 rounded-md border border-zinc-200 bg-white px-3 text-sm"
          data-testid="time-custom-input" />
      )}
    </div>
  );
}

export function ConflictWarning({ staffIds, date }) {
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => {
    if (!staffIds?.length || !date) { setConflicts([]); return; }
    const check = async () => {
      try {
        const { data } = await api.get('/jobs/conflicts', {
          params: { staff_ids: staffIds.join(','), date }
        });
        setConflicts(data.conflicts || []);
      } catch { setConflicts([]); }
    };
    check();
  }, [staffIds, date]);

  if (conflicts.length === 0) return null;

  return (
    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="conflict-warning">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <span className="text-sm font-semibold text-amber-800">
          Scheduling Conflict ({conflicts.length} existing job{conflicts.length > 1 ? 's' : ''})
        </span>
      </div>
      <div className="space-y-1.5">
        {conflicts.map(job => (
          <div key={job.id} className="flex items-center justify-between text-xs">
            <span className="text-amber-900 font-medium truncate flex-1">{job.title}</span>
            <div className="flex items-center gap-2 text-amber-700 flex-shrink-0">
              {job.scheduled_time_start && <span>{job.scheduled_time_start}-{job.scheduled_time_end}</span>}
              <span>{job.address?.split(',')[0] || ''}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ScheduleRecommendations({ address, staffIds, onSelectDate, preferredDate }) {
  const [recs, setRecs] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchRecs = useCallback(async () => {
    if (!address && !staffIds?.length) return;
    setLoading(true);
    try {
      const params = {
        address: address || '',
        staff_ids: (staffIds || []).join(','),
      };
      if (preferredDate) params.preferred_date = preferredDate;
      const { data } = await api.get('/jobs/recommend', { params });
      setRecs(data);
    } catch { setRecs(null); }
    finally { setLoading(false); }
  }, [address, staffIds, preferredDate]);

  useEffect(() => { fetchRecs(); }, [fetchRecs]);

  if (loading || !recs) return null;
  const hasRecs = recs.next_available || recs.efficient_dates?.length > 0;
  if (!hasRecs) return null;

  return (
    <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg space-y-3" data-testid="schedule-recommendations">
      <div className="flex items-center gap-2">
        <Zap className="w-4 h-4 text-sky-600" />
        <span className="text-sm font-semibold text-sky-800">Smart Scheduling</span>
      </div>

      {recs.next_available && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-sky-700">
            <Clock className="w-3 h-3" />
            <span>Next available: <strong>{recs.next_available.date} at {recs.next_available.time}</strong></span>
          </div>
          <Button type="button" variant="outline" size="sm" className="text-xs h-6 px-2 border-sky-300 text-sky-700 hover:bg-sky-100"
            onClick={() => onSelectDate(recs.next_available.date, recs.next_available.time)}
            data-testid="rec-next-available">
            Use
          </Button>
        </div>
      )}

      {recs.efficient_dates?.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-sky-600 mb-1.5">
            <MapPin className="w-3 h-3 inline mr-0.5" /> Crew Already in Area
          </p>
          <div className="space-y-1">
            {recs.efficient_dates.map((rec) => {
              const recKey = `${rec.date}-${rec.reason || 'area'}-${rec.matching_area_jobs || 0}`;
              return <div key={recKey} className="flex items-center justify-between p-1.5 bg-white rounded border border-sky-100">
                <div className="text-xs">
                  <span className="font-semibold text-sky-900">{rec.date}</span>
                  <span className="text-sky-600 ml-1.5">
                    {rec.reason} ({rec.matching_area_jobs} job{rec.matching_area_jobs > 1 ? 's' : ''})
                  </span>
                </div>
                <Button type="button" variant="ghost" size="sm" className="text-xs h-6 px-2 text-sky-700 hover:bg-sky-100"
                  onClick={() => onSelectDate(rec.date, '')}
                  data-testid={`rec-efficient-${recKey}`}>
                  <CalendarCheck className="w-3 h-3" />
                </Button>
              </div>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}
